
import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

export const useThumbnailExtraction = () => {
  const queryClient = useQueryClient();
  
  /**
   * Extracts a thumbnail for a specific content item
   * using the same extract-content-metadata function as the web clipper
   */
  const extractThumbnailForContent = useCallback(async (contentId: string, clipLevel: string = 'soft') => {
    try {
      console.log(`Requesting thumbnail extraction for content ID: ${contentId} with clip level: ${clipLevel}`);
      
      // First check if this content has a manual thumbnail that should be preserved
      const { data: contentData, error: contentError } = await supabase
        .from('content_items')
        .select('metadata, thumbnail_url')
        .eq('id', contentId)
        .single();
      
      if (contentError) {
        console.error('Error checking content metadata:', contentError);
        return { success: false, error: contentError };
      }
      
      // Skip extraction if this is a manual thumbnail and we're not forcing
      const metadata = contentData?.metadata as Record<string, any> | null;
      if (metadata && metadata.thumbnail_source === 'manual' && clipLevel !== 'hard') {
        console.log('Skipping extraction for manual thumbnail');
        toast.info('This content has a manually set thumbnail');
        return { success: false, skipped: true, reason: 'manual_thumbnail' };
      }
      
      // Use the extract-content-metadata function with the specified clip level
      const { data, error } = await supabase.functions.invoke('extract-content-metadata', {
        body: { 
          contentId, 
          clipLevel, // Use the specified clip level (default: soft)
          skipManual: clipLevel !== 'hard', // Only skip manual thumbnails if not hard clip
          forceRefresh: clipLevel === 'hard' // Only force refresh for hard clip
        }
      });
      
      if (error) {
        console.error('Error invoking content metadata extraction:', error);
        return { success: false, error };
      }
      
      console.log('Thumbnail extraction response:', data);
      
      if (data.success) {
        // Invalidate queries to refresh UI with new thumbnail
        queryClient.invalidateQueries({ queryKey: ['contentItems'] });
        const message = clipLevel === 'soft' ? 'Thumbnail extracted (preserving your title/description)' : 'Thumbnail extracted successfully';
        toast.success(message);
        return { success: true, thumbnailUrl: data.thumbnailUrl || contentData?.thumbnail_url };
      } else if (data.skipped) {
        toast.info('Thumbnail extraction skipped - manual thumbnail detected');
        return { success: false, skipped: true };
      } else {
        toast.error('Could not extract thumbnail from this content');
        return { success: false };
      }
    } catch (error) {
      console.error('Error in thumbnail extraction:', error);
      toast.error('Failed to extract thumbnail');
      return { success: false, error };
    }
  }, [queryClient]);
  
  /**
   * Refreshes thumbnails for content items with missing thumbnails
   * using the extract-content-metadata function
   */
  const refreshMissingThumbnails = useCallback(async () => {
    try {
      console.log('Refreshing missing thumbnails');
      
      // Use the extract-content-metadata function with batch processing
      const { data, error } = await supabase.functions.invoke('extract-content-metadata', {
        body: { 
          processAll: true,
          clipLevel: 'soft', // Use soft clip for batch processing to preserve existing data
          skipManual: true,
          forceRefresh: false // Don't force refresh for batch operations
        }
      });
      
      if (error) {
        console.error('Error processing content metadata:', error);
        toast.error('Failed to refresh thumbnails');
        return { success: false, error };
      }
      
      // Always invalidate queries to refresh the UI with any new thumbnails
      queryClient.invalidateQueries({ queryKey: ['contentItems'] });
      
      if (data && data.results && data.results.length > 0) {
        const successCount = data.results.filter((r: any) => r.success).length;
        const skippedCount = data.results.filter((r: any) => r.skipped).length;
        
        if (successCount > 0) {
          toast.success(`Refreshed ${successCount} thumbnails (preserving existing titles/descriptions)`);
        } else if (skippedCount > 0) {
          toast.info(`Skipped ${skippedCount} manual thumbnails`);
        } else {
          toast.info('No new thumbnails were found');
        }
        return { success: true, count: successCount, skipped: skippedCount };
      } else {
        toast.info('No content items needed thumbnails');
        return { success: true, count: 0 };
      }
    } catch (error) {
      console.error('Error refreshing thumbnails:', error);
      toast.error('Failed to refresh thumbnails');
      return { success: false, error };
    }
  }, [queryClient]);
  
  /**
   * Processes all pending thumbnail extractions
   * using the extract-content-metadata function
   */
  const processAllPendingThumbnails = useCallback(async () => {
    try {
      console.log('Processing all pending thumbnails');
      
      const { data, error } = await supabase.functions.invoke('extract-content-metadata', {
        body: { 
          processAll: true,
          clipLevel: 'soft', // Use soft clip to preserve existing data
          skipManual: true,
          priority: 'missing' // Prioritize items without thumbnails
        }
      });
      
      if (error) {
        console.error('Error processing pending thumbnails:', error);
        return { success: false, error };
      }
      
      if (data && data.results && data.results.length > 0) {
        const successCount = data.results.filter((r: any) => r.success).length;
        const skippedCount = data.results.filter((r: any) => r.skipped).length;
        
        if (successCount > 0) {
          // Invalidate queries to refresh the UI with new thumbnails
          queryClient.invalidateQueries({ queryKey: ['contentItems'] });
          console.log(`Extracted ${successCount} thumbnails, skipped ${skippedCount} manual thumbnails`);
        }
      }
      
      return { success: true, results: data.results };
    } catch (error) {
      console.error('Error in batch processing thumbnails:', error);
      return { success: false, error };
    }
  }, [queryClient]);

  return {
    extractThumbnailForContent,
    processAllPendingThumbnails,
    refreshMissingThumbnails
  };
};
